import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Card,
  CardMedia,
  CardActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { apiClient } from '../../lib/apiClient';
import LoadingOverlay from '../../components/common/LoadingOverlay';

function UserDisplay({ user }) {
  const [church, setChurch] = useState(null);
  const [bannerImages, setBannerImages] = useState([]);
  const [quoteOfTheDay, setQuoteOfTheDay] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fileInputKey, setFileInputKey] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const myChurches = await apiClient.getMyChurches(user.id);
        if (myChurches.length === 0) {
          setError('You are not assigned to any church');
          return;
        }

        const currentChurch = myChurches[0];
        setChurch(currentChurch);

        // Fetch user display settings
        const settings = await apiClient.getUserDisplaySettings(currentChurch.id);
        setBannerImages(settings.bannerImages || []);
        setQuoteOfTheDay(settings.quoteOfTheDay || '');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const handleBannerUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const result = await apiClient.uploadBannerImage(church.id, user.id, file);
      setBannerImages(result.bannerImages || []);
      setSuccess('Banner image uploaded successfully');
      setFileInputKey(prev => prev + 1); // Reset file input
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteBanner = async (bannerUrl) => {
    if (!window.confirm('Are you sure you want to delete this banner image?')) {
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const result = await apiClient.deleteBannerImage(church.id, user.id, bannerUrl);
      setBannerImages(result.bannerImages || []);
      setSuccess('Banner image deleted successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveQuote = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await apiClient.updateUserDisplaySettings(church.id, user.id, quoteOfTheDay);
      setSuccess('Quote of the day saved successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !church) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, mb: 3 }}>
        User Display Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <LoadingOverlay open={uploading} message="Uploading banner image..." />

      {/* Slider Banner Section */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, maxWidth: { xs: '100%', md: 800 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Slider Banner
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload banner images that will be displayed on the church user's home page
          </Typography>

          {/* Banner Image Upload */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              key={fileInputKey}
              accept="image/*"
              style={{ display: 'none' }}
              id="banner-upload-input"
              type="file"
              onChange={handleBannerUpload}
            />
            <label htmlFor="banner-upload-input">
              <Button
                variant="contained"
                component="span"
                startIcon={<AddPhotoAlternateIcon />}
                disabled={uploading}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Upload Banner Image
              </Button>
            </label>
          </Box>

          {/* Banner Images Grid */}
          {bannerImages.length > 0 && (
            <Grid container spacing={2}>
              {bannerImages.map((bannerUrl, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="200"
                      image={bannerUrl}
                      alt={`Banner ${index + 1}`}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardActions sx={{ justifyContent: 'center' }}>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteBanner(bannerUrl)}
                        disabled={uploading}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {bannerImages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No banner images uploaded yet
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Quote of the Day Section */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: { xs: '100%', md: 800 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Quote of the Day
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter the quote of the day that will be displayed on the church user's home page
          </Typography>

          <TextField
            label="Quote of the Day"
            multiline
            rows={4}
            value={quoteOfTheDay}
            onChange={(e) => setQuoteOfTheDay(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="Enter an inspiring quote..."
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />

          <Box>
            <Button
              variant="contained"
              onClick={handleSaveQuote}
              disabled={saving}
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              {saving ? <CircularProgress size={24} /> : 'Save Quote'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default UserDisplay;

